import React, { useEffect, useMemo, useRef, useState, useCallback } from 'react';
import MapGL, { Source, Layer, Popup, WebMercatorViewport } from 'react-map-gl';
import { bbox as turfBbox } from '@turf/turf';
import { useApi } from '../../hooks';
// Deep-import path on purpose: the top-level `../../ui` barrel (and its
// `./components` barrel) re-exports `./Mapbox`, which eagerly requires
// `mapbox-gl` — a module that throws in jsdom. See the same comment in
// PmlSnapshotPage.jsx / ConcernRegister.jsx.
import { Select } from '../../ui/components/Select';
import { MenuItem } from '../../ui/components/MenuItem';
import { SCALES, METRICS, STATUS_COLORS, fillExpression, statusLineExpression, formatUsd, peakOf, metricEnabled } from './hexMapStyles';

const TOKEN_KEY = 'VITE_MAPBOX_TOKEN';
const DEFAULT_VIEWPORT = { latitude: 38.5, longitude: -96.5, zoom: 3.6 };
const LATTICE_LINE_COLOR = '#B9A7D8';
const LATTICE_ZOOM_THRESHOLD = 6;
const LATTICE_DEBOUNCE_MS = 400;
const LATTICE_EXPAND_FACTOR = 0.5; // grow the fetched bbox ~50% so small pans don't refetch

const LATTICE_SOURCE_ID = 'pml-lattice';
const LATTICE_LINE_LAYER = 'pml-lattice-line';
const CELLS_SOURCE_ID = 'pml-cells';
const CELLS_FILL_LAYER = 'pml-cells-fill';
const CELLS_STATUS_LINE_LAYER = 'pml-cells-status-line';

const STATUS_LINE_WIDTH = ['match', ['get', 'status'], 'red', 2, 'amber', 1.5, 'green', 0.5, 0.5];

const isNil = (v) => v === null || v === undefined;

const formatMetricValue = (metricKey, value) => {
  const metric = METRICS[metricKey];
  if (!metric) return '—';
  if (metric.kind === 'status') return value || '—';
  if (metric.format === 'int') return isNil(value) ? '—' : Math.round(Number(value)).toLocaleString('en-US');
  return formatUsd(value);
};

// Flatten react-map-gl's WebMercatorViewport#getBounds() ([[west, south], [east,
// north]]) into the [minLon, minLat, maxLon, maxLat] shape the lattice endpoint
// (and turf) expect.
const flattenBounds = (bounds) => [bounds[0][0], bounds[0][1], bounds[1][0], bounds[1][1]];

const expandBbox = (box, factor) => {
  const [minLon, minLat, maxLon, maxLat] = box;
  const w = maxLon - minLon;
  const h = maxLat - minLat;
  return [minLon - (w * factor) / 2, minLat - (h * factor) / 2, maxLon + (w * factor) / 2, maxLat + (h * factor) / 2];
};

// True when `inner` is fully within `outer` — i.e. the viewport hasn't left the
// area we already fetched, so no refetch is needed.
export const bboxContains = (outer, inner) => {
  if (!outer || !inner) return false;
  return inner[0] >= outer[0] && inner[1] >= outer[1] && inner[2] <= outer[2] && inner[3] <= outer[3];
};

// Pure decision helper for the 5/10mi lattice fetch: `bboxByScale` is a
// { [scaleMi]: lastFetchedExpandedBbox } map. Keying by scale (rather than one
// shared bbox ref) is what makes toggling 5<->10 at a fixed viewport correctly
// refetch — the other scale's last-fetched bbox lives under a different key,
// so `bboxByScale[scaleMi]` is undefined (or a different-scale grid) until
// this scale itself has been fetched at (a superset of) the current bbox.
export const shouldRefetchLattice = (bboxByScale, scaleMi, currentBbox) => !bboxContains(bboxByScale && bboxByScale[scaleMi], currentBbox);

// Standard React error boundary — catches a MapGL/mapbox-gl render-time throw
// (e.g. no WebGL) and swaps in the same fallback the missing-token path uses,
// so this component never crashes its parent page.
class MapErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch() {
    // Intentionally silent — the fallback UI communicates the failure.
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

// HexMap owns lattice fetching internally (Task 13 controller override): at
// scale 25/50 it fetches the lattice once per scale per mount (cached in a
// ref, no bbox needed — the server allows an unbounded request at those
// scales). At 5/10 the server requires a bbox and 400s without one, so we only
// fetch once the user has zoomed in past `LATTICE_ZOOM_THRESHOLD`, debounced
// ~400ms after the viewport settles, and only refetch when the viewport has
// panned/zoomed outside the last-fetched (deliberately over-fetched by ~50%)
// bbox. Below the zoom threshold at 5/10, no lattice layer is rendered at all.
const HexMap = ({ cells, metric, scaleMi, onScaleChange, onMetricChange, onCellClick, focusCell }) => {
  const { getPmlLattice } = useApi() || {};
  const token = import.meta.env[TOKEN_KEY];

  const meta = cells?.meta || null;
  const features = useMemo(() => cells?.features || [], [cells]);
  const peak = useMemo(() => peakOf(features, METRICS[metric]?.prop), [features, metric]);

  const [viewport, setViewport] = useState(DEFAULT_VIEWPORT);
  const [hoverInfo, setHoverInfo] = useState(null);
  const [lattice, setLattice] = useState(null);

  const containerRef = useRef(null);
  const latticeCacheRef = useRef({}); // { [scaleMi]: FeatureCollection } — all scales
  const fetchedBboxRef = useRef({}); // { [scaleMi]: last fetched (expanded) bbox } — 5/10 only
  const debounceRef = useRef(null);

  const getContainerSize = useCallback(() => {
    const el = containerRef.current;
    if (el && el.clientWidth && el.clientHeight) return { width: el.clientWidth, height: el.clientHeight };
    return { width: 800, height: 600 };
  }, []);

  // Coarse scales (25/50): fetch once per scale, cached for the life of the mount.
  useEffect(() => {
    if (![25, 50].includes(scaleMi)) return undefined;
    if (!getPmlLattice) return undefined;
    if (latticeCacheRef.current[scaleMi]) {
      setLattice(latticeCacheRef.current[scaleMi]);
      return undefined;
    }
    let cancelled = false;
    (async () => {
      try {
        const fc = await getPmlLattice(scaleMi);
        if (cancelled) return;
        latticeCacheRef.current[scaleMi] = fc;
        setLattice(fc);
      } catch (e) {
        // Lattice fetch failure is silent — the hex map itself still works.
      }
    })();
    return () => {
      cancelled = true;
    };
    // getPmlLattice deliberately omitted — useApi() methods are recreated on
    // every ApiProvider re-render, and including them here would risk a
    // spurious refetch unrelated to scaleMi changing. Same convention
    // PmlSnapshotPage.jsx's effects use.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleMi]);

  // Fine scales (5/10): bbox-required, zoom-gated, debounced, refetch-on-exit.
  // Both the fetched-bbox bookkeeping (`fetchedBboxRef`) and the lattice data
  // itself (`latticeCacheRef`) are keyed by `scaleMi`, so toggling 5<->10 at a
  // fixed viewport shows this scale's own cached grid immediately (or nothing,
  // if this scale hasn't been fetched yet) rather than leaving the other
  // scale's grid on screen — see `shouldRefetchLattice`.
  useEffect(() => {
    if (![5, 10].includes(scaleMi)) return undefined;
    if (viewport.zoom < LATTICE_ZOOM_THRESHOLD) {
      setLattice(null);
      return undefined;
    }
    // Show this scale's own cached lattice (if any) right away, so switching
    // scales doesn't leave the previous scale's grid visible while the
    // debounce below decides whether a fetch is needed.
    setLattice(latticeCacheRef.current[scaleMi] || null);

    if (!getPmlLattice || typeof WebMercatorViewport !== 'function') return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      let currentBbox;
      try {
        const { width, height } = getContainerSize();
        const wmv = new WebMercatorViewport({ ...viewport, width, height });
        currentBbox = flattenBounds(wmv.getBounds());
      } catch (e) {
        return;
      }
      if (!shouldRefetchLattice(fetchedBboxRef.current, scaleMi, currentBbox)) return;
      const expanded = expandBbox(currentBbox, LATTICE_EXPAND_FACTOR);
      (async () => {
        try {
          const fc = await getPmlLattice(scaleMi, expanded);
          fetchedBboxRef.current = { ...fetchedBboxRef.current, [scaleMi]: expanded };
          latticeCacheRef.current[scaleMi] = fc;
          setLattice(fc);
        } catch (e) {
          // Silent — no lattice this pass, map itself still works.
        }
      })();
    }, LATTICE_DEBOUNCE_MS);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // getPmlLattice deliberately omitted — same rationale as the coarse-scale
    // effect above (getContainerSize is a useCallback with stable deps, so
    // it's kept).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scaleMi, viewport, getContainerSize]);

  // focusCell: pan/zoom to the clicked register row's cell, if it's present in
  // the currently-loaded feature set (it may not be — e.g. a different scale's
  // cell — in which case this is a deliberate no-op rather than a crash).
  useEffect(() => {
    if (!focusCell || !features.length) return;
    const feature = features.find((f) => f?.properties?.cellId === focusCell.cellId);
    if (!feature) return;
    if (typeof WebMercatorViewport !== 'function') return;
    try {
      const [minLon, minLat, maxLon, maxLat] = turfBbox(feature);
      const { width, height } = getContainerSize();
      const wmv = new WebMercatorViewport({ ...viewport, width, height });
      const fitted = wmv.fitBounds(
        [
          [minLon, minLat],
          [maxLon, maxLat],
        ],
        { padding: 40 }
      );
      setViewport((v) => ({ ...v, ...fitted }));
    } catch (e) {
      // Bad/degenerate geometry — leave the viewport where it is.
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusCell, features]);

  const handleHover = (event) => {
    const feature = event?.features?.[0];
    if (!feature) {
      setHoverInfo(null);
      return;
    }
    setHoverInfo({ lngLat: event.lngLat, properties: feature.properties });
  };

  const handleClick = (event) => {
    const feature = event?.features?.[0];
    if (!feature) return;
    onCellClick(scaleMi, feature.properties?.cellId);
  };

  const subtitle = meta ? `${meta.occupiedCount ?? 0} occupied cells · peak ${formatUsd(meta.peakPml)}` : 'Loading cells…';

  const fallback = (
    <div style={S.fallback} data-testid="hexmap-fallback">
      <div style={S.subtitle}>{subtitle}</div>
      <div style={S.fallbackNote}>Map unavailable</div>
    </div>
  );

  if (!token) return fallback;

  return (
    <MapErrorBoundary fallback={fallback}>
      <div style={S.wrap} ref={containerRef} data-testid="hexmap">
        <div style={S.header}>
          <div style={S.subtitle}>{subtitle}</div>
          <div style={S.scaleButtons}>
            {SCALES.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => onScaleChange(s)}
                style={{ ...S.scaleButton, ...(s === scaleMi ? S.scaleButtonActive : {}) }}
              >
                {s} mi
              </button>
            ))}
          </div>
          {/* Fixed width so the control doesn't resize to the selected label and the
              dropdown (which the Select clamps to the control's measured width) is wide
              enough for the longest option ("1-in-250 PML") instead of truncating it. */}
          <Select value={metric} onChangeValue={onMetricChange} styles={{ textInput: { width: 200 } }}>
            {Object.keys(METRICS).map((key) => (
              <MenuItem key={key} value={key} disabled={!metricEnabled(key, meta, features)}>
                {METRICS[key].label}
              </MenuItem>
            ))}
          </Select>
        </div>

        {meta && meta.unmappableCount > 0 && (
          <div style={S.banner}>{meta.unmappableCount} location(s) could not be mapped to a cell and are excluded from this view.</div>
        )}

        <div style={S.mapWrap}>
          <MapGL
            {...viewport}
            width="100%"
            height="100%"
            mapStyle="mapbox://styles/mapbox/light-v10"
            mapboxApiAccessToken={token}
            interactiveLayerIds={[CELLS_FILL_LAYER]}
            onViewportChange={setViewport}
            onHover={handleHover}
            onClick={handleClick}
          >
            {lattice && (
              <Source id={LATTICE_SOURCE_ID} type="geojson" data={lattice}>
                <Layer id={LATTICE_LINE_LAYER} type="line" paint={{ 'line-color': LATTICE_LINE_COLOR, 'line-width': 0.4, 'line-opacity': 0.5 }} />
              </Source>
            )}
            <Source id={CELLS_SOURCE_ID} type="geojson" data={cells || { type: 'FeatureCollection', features: [] }}>
              <Layer id={CELLS_FILL_LAYER} type="fill" paint={{ 'fill-color': fillExpression(metric, peak), 'fill-opacity': 0.78 }} />
              <Layer id={CELLS_STATUS_LINE_LAYER} type="line" paint={{ 'line-color': statusLineExpression(), 'line-width': STATUS_LINE_WIDTH }} />
            </Source>
            {hoverInfo && (
              <Popup longitude={hoverInfo.lngLat[0]} latitude={hoverInfo.lngLat[1]} closeButton={false} closeOnClick={false} anchor="top">
                <div style={S.popup}>
                  <div style={S.popupPlace}>{hoverInfo.properties.place || hoverInfo.properties.cellId}</div>
                  <div style={S.popupCellId}>{hoverInfo.properties.cellId}</div>
                  <div>{formatMetricValue(metric, hoverInfo.properties[METRICS[metric]?.prop])}</div>
                </div>
              </Popup>
            )}
          </MapGL>
        </div>
      </div>
    </MapErrorBoundary>
  );
};

const S = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 8, height: '100%' },
  header: { display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' },
  subtitle: { fontSize: 13, color: '#637381' },
  scaleButtons: { display: 'flex', gap: 4 },
  scaleButton: {
    border: '1px solid #DFE3E8',
    background: '#fff',
    borderRadius: 6,
    padding: '4px 10px',
    fontSize: 12,
    cursor: 'pointer',
    color: '#637381',
  },
  scaleButtonActive: { background: STATUS_COLORS.green, borderColor: STATUS_COLORS.green, color: '#fff', fontWeight: 600 },
  banner: { background: '#FFF4E5', color: '#8A5A00', padding: '6px 12px', borderRadius: 6, fontSize: 12 },
  mapWrap: { flex: 1, minHeight: 420, borderRadius: 8, overflow: 'hidden', border: '1px solid #DFE3E8', position: 'relative' },
  fallback: { padding: 24, color: '#637381', border: '1px solid #DFE3E8', borderRadius: 8 },
  fallbackNote: { marginTop: 8, fontWeight: 600 },
  popup: { fontSize: 12, display: 'flex', flexDirection: 'column', gap: 2 },
  popupPlace: { fontWeight: 600 },
  popupCellId: { color: '#637381' },
};

export default HexMap;
